import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { NAVY, GOLD } from '../utils/constants';
import GalleryManager from './GalleryManager';

// Website Content — internal control panel for the per-cruise text the PUBLIC
// SITE displays. Writes cruise_settings.public_description and .public_terms,
// one row per cruise. These are SEPARATE from the printed ticket's important-
// information block — this screen touches no ticket logic.

const cardStyle = {
  background: '#fff',
  border: '1px solid var(--color-border)',
  borderRadius: 14,
  padding: '22px 24px',
  maxWidth: 640,
  boxShadow: '0 1px 6px rgba(10,22,40,0.06)',
  marginBottom: 16,
};

const lbl = {
  display: 'block',
  fontSize: '0.75rem',
  fontWeight: 700,
  color: 'var(--color-ink-secondary)',
  marginBottom: 4,
};

const inp = (extra = {}) => ({
  width: '100%',
  padding: '9px 12px',
  border: '1.5px solid var(--color-border-strong)',
  borderRadius: 8,
  fontSize: '0.88rem',
  outline: 'none',
  boxSizing: 'border-box',
  background: '#fff',
  color: NAVY,
  ...extra,
});

function TextAreaField({ label, value, onChange, rows, placeholder }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={lbl}>{label}</label>
      <textarea
        rows={rows}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...inp(), resize: 'vertical', lineHeight: 1.5 }}
      />
    </div>
  );
}

export default function WebsiteContent({ onNavigate }) {
  const [cruises, setCruises] = useState([]);   // baseline rows from the DB
  const [forms, setForms]     = useState({});   // { [id]: { description, terms } } — editable
  const [decks, setDecks]     = useState([]);   // live deck names from table_inventory
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [savedId, setSavedId]   = useState(null);
  const [errorId, setErrorId]   = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    Promise.all([
      supabase
        .from('cruise_settings')
        .select('id, cruise_name, cruise_code, public_description, public_terms, has_upper_deck, has_middle_deck')
        .eq('is_active', true)
        .order('sort_order'),
      // Deck list read LIVE from table_inventory — nothing hardcoded. A deck
      // looks different per cruise (sunset light vs dinner), so deck galleries
      // are per-cruise-per-deck: owner_type 'cruise_deck', owner_key
      // "CODE|Deck Name". The live deck names here feed each cruise's own decks.
      supabase.from('table_inventory').select('deck').order('deck'),
    ]).then(([{ data: cs }, { data: ti }]) => {
      const rows = cs || [];
      setCruises(rows);
      const seed = {};
      rows.forEach(r => { seed[r.id] = { description: r.public_description || '', terms: r.public_terms || '' }; });
      setForms(seed);

      // Distinct deck names, preserving query order.
      const seen = new Set();
      const deckNames = [];
      (ti || []).forEach(r => {
        if (r.deck && !seen.has(r.deck)) { seen.add(r.deck); deckNames.push(r.deck); }
      });
      setDecks(deckNames);

      setLoading(false);
    });
  }, []);

  const handleChange = (id, field, val) => {
    if (savedId === id) setSavedId(null);
    if (errorId === id) { setErrorId(null); setErrorMsg(''); }
    setForms(prev => ({ ...prev, [id]: { ...prev[id], [field]: val } }));
  };

  const handleSave = async (id) => {
    setSavingId(id);
    setErrorId(null);
    setErrorMsg('');
    const { description, terms } = forms[id];
    const { error } = await supabase
      .from('cruise_settings')
      .update({ public_description: description, public_terms: terms })
      .eq('id', id);
    setSavingId(null);
    if (error) {
      // Surface the error; keep what the user typed (forms state is untouched).
      setErrorId(id);
      setErrorMsg(error.message || 'Could not save. Please try again.');
      return;
    }
    // Update the baseline so the saved values stick.
    setCruises(prev => prev.map(c => c.id === id ? { ...c, public_description: description, public_terms: terms } : c));
    setSavedId(id);
    setTimeout(() => setSavedId(cur => (cur === id ? null : cur)), 2500);
  };

  // The live deck names this cruise actually has, matched to its
  // has_upper_deck / has_middle_deck flags — same approach the rest of the app
  // uses (see CruiseSettings). Preserves the live table_inventory deck order.
  const decksForCruise = (cruise) =>
    decks.filter(d => {
      const k = d.toLowerCase();
      if (k.includes('upper'))  return !!cruise.has_upper_deck;
      if (k.includes('middle')) return !!cruise.has_middle_deck;
      return false;
    });

  if (loading) {
    return (
      <div>
        <div className="back-row">
          <button className="back-btn" onClick={() => onNavigate('settings')}>← Settings</button>
        </div>
        <p style={{ color: 'var(--color-ink-faint)', padding: 20 }}>Loading…</p>
      </div>
    );
  }

  return (
    <div>
      <div className="back-row">
        <button className="back-btn" onClick={() => onNavigate('settings')}>← Settings</button>
        <h2 style={{ fontSize: '1rem', fontWeight: 700, color: NAVY }}>Website Content</h2>
      </div>

      <p style={{ fontSize: '0.8rem', color: 'var(--color-ink-muted)', maxWidth: 640, marginBottom: 20 }}>
        Text shown on the public booking website, per cruise. Saved here, displayed there.
        These are separate from the printed ticket's important-information block.
      </p>

      {cruises.length === 0 && (
        <p style={{ color: 'var(--color-ink-faint)', padding: 20 }}>No active cruises found.</p>
      )}

      {cruises.map(c => {
        const f = forms[c.id] || { description: '', terms: '' };
        const isSaving = savingId === c.id;
        const isSaved  = savedId === c.id;
        return (
          <div key={c.id} style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
              <h3 style={{ fontSize: '1rem', fontWeight: 800, color: NAVY, margin: 0 }}>{c.cruise_name}</h3>
              <span style={{ fontSize: '0.68rem', fontWeight: 700, color: 'var(--color-ink-muted)', background: 'var(--color-sunken)', border: '1px solid var(--color-border)', padding: '2px 8px', borderRadius: 999, letterSpacing: '0.4px' }}>
                {c.cruise_code}
              </span>
            </div>

            <TextAreaField
              label="Public description"
              value={f.description}
              onChange={(val) => handleChange(c.id, 'description', val)}
              rows={4}
              placeholder="Describe this cruise for the public website…"
            />
            <TextAreaField
              label="Terms & conditions"
              value={f.terms}
              onChange={(val) => handleChange(c.id, 'terms', val)}
              rows={6}
              placeholder="Public terms & conditions for this cruise…"
            />

            {errorId === c.id && (
              <p style={{ color: '#dc2626', fontSize: '0.85rem', marginBottom: 12 }}>{errorMsg}</p>
            )}

            <button
              onClick={() => handleSave(c.id)}
              disabled={isSaving}
              style={{
                background: isSaved ? '#15803d' : NAVY,
                color: isSaved ? '#fff' : GOLD,
                border: 'none', borderRadius: 10,
                padding: '10px 24px', fontSize: '0.88rem', fontWeight: 700,
                cursor: isSaving ? 'not-allowed' : 'pointer',
                opacity: isSaving ? 0.8 : 1,
                transition: 'background 0.2s',
              }}
            >
              {isSaving ? 'Saving…' : isSaved ? '✓ Saved' : 'Save'}
            </button>

            <div style={{ borderTop: '1px solid var(--color-border)', margin: '20px 0 16px' }} />
            <label style={{ ...lbl, marginBottom: 10 }}>Photo gallery</label>
            <GalleryManager ownerType="cruise" ownerKey={c.cruise_code} />

            {decksForCruise(c).map(deck => (
              <div key={deck}>
                <div style={{ borderTop: '1px solid var(--color-border)', margin: '20px 0 16px' }} />
                <label style={{ ...lbl, marginBottom: 10 }}>{deck} photos</label>
                <GalleryManager ownerType="cruise_deck" ownerKey={`${c.cruise_code}|${deck}`} />
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}
